<template>
  <div data-component="ui-payout-request-history">
    <TableGrid
      v-bind="tableProps"
      autoSwitchMode
    >
      <template #top>
        <div class="d-flex flex-wrap gap-2 ">
          <v-select
            v-model="selectedWalletId"
            :items="wallets"
            minWidth="260"
            :loading="walletsLoading"
            :label="lt('wallet_filter')"
            itemTitle="title"
            itemValue="id"
            hideDetails
            prependInnerIcon="mdi-wallet"
            clearable
            variant="outlined"
            density="comfortable"
            @update:model-value="onFiltersChange"
          >
            <template #item="{ item, props }">
              <v-list-item v-bind="props" :title="item.raw.title">
                <v-list-item-subtitle>
                  ({{ item.raw.currency_id }} {{ item.raw.balance }})
                </v-list-item-subtitle>
              </v-list-item>
            </template>
          </v-select>

          <v-select
            v-model="selectedStatusId"
            minWidth="260"
            :items="statusOptions"
            :label="lt('status_filter')"
            itemTitle="title"
            itemValue="value"
            hideDetails
            prependInnerIcon="mdi-information-outline"
            clearable
            variant="outlined"
            density="comfortable"
            @update:model-value="onFiltersChange"
          />
        </div>
      </template>
      <template #expansion="{ item }">
        <v-expansion-panel>
          <v-expansion-panel-title>
            <div class="text-caption">
              <span class="mr-4">{{ item.requestDate?.presentable?.split(/[,\s]+/)[0] }}</span>
              <v-chip class="mr-4" :color="statusColor(String(item?.statusCode?.raw))">
                {{ item.status?.presentable }}
              </v-chip>
              <span><b>{{ item.amount?.presentable }}</b> {{ item.currency?.presentable }}</span>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text class="pt-4">
            <div><b>{{ headerTitles['requestId'] }}:</b> {{ item.requestId }}</div>
            <div><b>{{ headerTitles['requestDate'] }}:</b> {{ item.requestDate }} </div>
            <div><b>{{ headerTitles['paymentSystem'] }}:</b> {{ item.paymentSystem }}</div>
            <div><b>{{ headerTitles['wallet'] }}:</b> {{ item.wallet }}</div>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </template>

      <template #status="{ item }">
        <v-chip :color="statusColor(String(item?.statusCode?.raw))">
          {{ item?.status?.presentable }}
        </v-chip>
      </template>

      <template #amount="{ item }">
        <b>{{ item?.amount?.presentable }}</b> {{ item?.currency?.presentable }}
      </template>

      <template #wallet="{ item }">
        <div class="d-flex align-center">
          <v-icon class="mr-2" size="small">mdi-wallet</v-icon>
          <span>{{ item?.wallet?.presentable }}</span>
        </div>
      </template>
    </TableGrid>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';

import i18n from '@/i18n';
import { TableGridItem, TableGridPaginationState } from "@/models/table-grid-interface";

import { useAwaitAccountId, useLocalization } from '@/composables';
import { useTableGrid } from '@/composables/useTableGrid';
import { useWalletOperations } from '@/composables/useWalletOperations';

import { useEventBus } from '@/pinia/useEventBus';
import { PayoutVM, useAccountPayoutApi, FilterItem } from '@ui-api3-sdk/api/api3';

import TableGrid from './base/TableGrid.vue';

const { lt, la, reactiveList } = useLocalization('uiPayoutRequestHistory');

interface PayoutRequestHistoryProps {
  accountId?: number;
  walletId?: number;
}

const props = withDefaults(defineProps<PayoutRequestHistoryProps>(),{
  accountId: undefined,
  walletId: undefined,
});

const selectedWalletId = ref<number | undefined>(props.walletId);
const selectedStatusId = ref<string | undefined>(undefined);

// Initialize wallet operations for the select
const { wallets, loading: walletsLoading, fetchWallets } = useWalletOperations();

// Status options with mapped labels
const statusOptions = computed(() => [
  { value: 'new', title: `${lt('status_pending')} (${lt('status_detail_new')})` },
  { value: 'processing', title: `${lt('status_pending')} (${lt('status_detail_processing')})` },
  { value: 'full_filled', title: `${lt('status_done')} (${lt('status_detail_full_filled')})` },
  { value: 'completed', title: `${lt('status_done')} (${lt('status_detail_completed')})` },
  { value: 'canceled', title: `${lt('status_canceled')}` },
  { value: 'error', title: `${lt('status_error')}` },
]);

const properties = ['requestId', 'requestDate', 'amount', 'paymentSystem', 'status', 'wallet'];
const headerTitles = reactiveList('p', properties);

const { tableProps, tableMethods } = useTableGrid({
  properties,
  headerTitles,
  fetch,
  maximumLimit: 500,
  unknownErrorMessage: lt('unknown_error'),
});

watch(() => useEventBus().payoutHistoryUpdated, () => {
  tableMethods.doFetch();
});

watch(() => useEventBus().walletHistoryUpdated, () => {
  fetchWallets();
});

function onFiltersChange() {
  tableMethods.doFetch();
}

async function fetch(properties: string[], pagination: TableGridPaginationState) {
  tableMethods.setLoading(true);

  const forAccountId = await useAwaitAccountId(props.accountId);

  const apiParams: any = {
    id: forAccountId,
    limit: pagination.limit,
    page: pagination.page - 1,
  };

  const requestOptions: any = {
    params: {
      orderBy: {
        'payout.created_at': 'DESC',
      },
      filter: [] as FilterItem[],
    },
  };

  // Add wallet filter if selected
  if (selectedWalletId.value) {
    requestOptions.params.filter.push({
      field: 'payment-rule.source_wallet_type_id',
      value: selectedWalletId.value.toString(),
      operation: '=',
    } as FilterItem);
  }

  // Add status filter if selected
  if (selectedStatusId.value) {
    requestOptions.params.filter.push({
      field: 'payout.internal_status',
      value: selectedStatusId.value,
      operation: '=',
    } as FilterItem);
  }

  const result = await useAccountPayoutApi().getAccountInvoiceList(apiParams, requestOptions);

  const tableItems = result.data.payload.list.
    // sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).
    map( (e) => mapPayment(e) );

  const totalItems = result.data.payload.total;

  tableMethods.setLoading(false);
  return { tableItems, totalItems };
}


function statusColor(statusCode: string) {
  switch (statusCode) {
    case 'pending':
      return 'primary';
    case 'done':
      return 'success';
    case 'canceled':
      return 'error';
    case 'error':
      return 'error';
  }
  return '';
}

function mapPayment(payment: PayoutVM): TableGridItem {


  function val(v: any) {
    return { presentable: String(v), raw: v };
  }

  const statusField = new Map([['processing', 'pending'],['new', 'pending'], ['full_filled', 'done'], ['completed', 'done'], ['canceled', 'canceled'], ['error', 'error']]);

  const statusCode = statusField.get(payment.internalStatus);
  const dateTime = new Date(payment.createdAt).toLocaleString(i18n.global.locale);
  //dayjs().format('{YYYY} MM-DD HH:mm:ss SSS [Z] A')

  // Extract wallet information
  const walletTitle = payment?.paymentRule?.sourceWalletType?.title ||
    payment?.paymentRule?.sourceWalletType?.currency_id ||
    'N/A';

  return {
    requestId: val(payment.id),
    requestDate: val(dateTime),
    amount: val(payment.buyerAmount),
    currency: val(payment?.paymentRule?.sourceWalletType?.currency_id),
    paymentSystem: val(payment.paymentRule.paymentSystem.title),
    status: val(lt( `status_${statusCode}` )),
    statusCode: val(statusCode),
    wallet: val(walletTitle),
  };
}

</script>

<style scoped>
.gap-2 {
  gap: 8px;
}
</style>
